import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, UserCheck, UserMinus, UserX, Users } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { useConfirm } from "#/components/common/ConfirmProvider";
import {
  useCancelEnrollment,
  useCancelProgram,
  useCompleteProgram,
  useDeleteProgram,
  useEnrollInProgram,
  useMarkAbsent,
  useMarkAttended,
  usePublishProgram,
  useTrainingProgram,
} from "../hooks";
import type { TrainingEnrollmentStatus, TrainingProgramStatus, TrainingPublishRequest } from "../types";

const P_STATUS: Record<TrainingProgramStatus, string> = {
  DRAFT:     "bg-slate-100 text-slate-700 ring-slate-200",
  PUBLISHED: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  COMPLETED: "bg-indigo-100 text-indigo-800 ring-indigo-200",
  CANCELLED: "bg-red-100 text-red-700 ring-red-200",
};

const E_STATUS: Record<TrainingEnrollmentStatus, string> = {
  ENROLLED:  "bg-amber-100 text-amber-700",
  ATTENDED:  "bg-emerald-100 text-emerald-700",
  ABSENT:    "bg-red-100 text-red-600",
  CANCELLED: "bg-slate-100 text-slate-500",
};

function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}
function fmtDT(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}
function extractMsg(err: unknown) {
  const e = err as any;
  return e?.response?.data?.message ?? e?.message ?? "Something went wrong.";
}

type PublishMode = "all" | "dept" | "specific" | null;

export default function TrainingProgramViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const progId = Number(id);

  const query    = useTrainingProgram(progId);
  const publish  = usePublishProgram();
  const enroll   = useEnrollInProgram();
  const cancel   = useCancelProgram();
  const complete = useCompleteProgram();
  const del      = useDeleteProgram();
  const attended = useMarkAttended();
  const absent   = useMarkAbsent();
  const cancelE  = useCancelEnrollment();

  const [publishMode, setPublishMode] = useState<PublishMode>(null);
  const [deptIds, setDeptIds]         = useState("");
  const [empIds, setEmpIds]           = useState("");
  const [enrollMode, setEnrollMode]   = useState<PublishMode>(null);
  const [error, setError]             = useState<string | null>(null);
  const confirm = useConfirm();

  if (query.isLoading) return <PageLoader label="Loading program…" />;
  if (query.isError || !query.data) return <ErrorState title="Not found" message="Program could not be loaded." />;

  const p = query.data;
  const busy = publish.isPending || enroll.isPending || cancel.isPending || complete.isPending || del.isPending;

  function parseIds(raw: string): number[] {
    return raw.split(/[\s,]+/).map(Number).filter((n) => !isNaN(n) && n > 0);
  }

  function buildPayload(mode: PublishMode): TrainingPublishRequest {
    if (mode === "all")      return { allEmployees: true };
    if (mode === "dept")     return { departmentIds: parseIds(deptIds) };
    if (mode === "specific") return { employeeIds: parseIds(empIds) };
    return {};
  }

  async function handlePublish() {
    setError(null);
    try {
      await publish.mutateAsync({ id: progId, payload: buildPayload(publishMode) });
      setPublishMode(null);
      query.refetch();
    } catch (err) { setError(extractMsg(err)); }
  }

  async function handleEnrollMore() {
    setError(null);
    try {
      await enroll.mutateAsync({ id: progId, payload: buildPayload(enrollMode) });
      setEnrollMode(null);
      query.refetch();
    } catch (err) { setError(extractMsg(err)); }
  }

  async function handleCancel() {
    if (!(await confirm("Cancel this program? All enrollments will be cancelled and employees notified."))) return;
    try { await cancel.mutateAsync(progId); query.refetch(); }
    catch (err) { setError(extractMsg(err)); }
  }

  async function handleComplete() {
    if (!(await confirm("Mark this program as completed?"))) return;
    try { await complete.mutateAsync(progId); query.refetch(); }
    catch (err) { setError(extractMsg(err)); }
  }

  async function handleDelete() {
    if (!(await confirm("Delete this program? This cannot be undone."))) return;
    try { await del.mutateAsync(progId); navigate(-1); }
    catch (err) { setError(extractMsg(err)); }
  }

  const enrollments = p.enrollments ?? [];
  const enrolled = enrollments.filter(e => e.status === "ENROLLED").length;
  const attended_ = enrollments.filter(e => e.status === "ATTENDED").length;
  const absent_   = enrollments.filter(e => e.status === "ABSENT").length;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800">← Back</button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{p.title}</h1>
          {p.programNo && <p className="text-xs text-slate-400 mt-0.5">{p.programNo}</p>}
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${P_STATUS[p.status]}`}>{p.status}</span>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {/* Details */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm text-sm">
        <h2 className="font-bold text-slate-700 mb-3 text-xs uppercase tracking-wide">Details</h2>
        {[
          ["Category",       p.categoryName],
          ["Provider",       p.providerName],
          ["Venue",          p.venue],
          ["Start date",     fmtDate(p.startDate)],
          ["End date",       fmtDate(p.endDate)],
          ["Estimated cost", p.estimatedCost != null ? `KES ${p.estimatedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : null],
          ["Published",      fmtDT(p.publishedAt)],
          ["Completed",      fmtDT(p.completedAt)],
        ].filter(([, v]) => v && v !== "—").map(([label, value]) => (
          <div key={label as string} className="grid grid-cols-3 gap-2 py-2 border-b last:border-0">
            <span className="font-medium text-slate-500">{label}</span>
            <span className="col-span-2 text-slate-800">{value}</span>
          </div>
        ))}
        {p.description && (
          <div className="pt-2 text-sm">
            <p className="font-medium text-slate-500 mb-1">Description</p>
            <p className="text-slate-700 whitespace-pre-wrap">{p.description}</p>
          </div>
        )}
      </div>

      {/* Enrollment stats */}
      {enrollments.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Enrolled",  value: enrolled,  color: "bg-amber-50  border-amber-200  text-amber-700"   },
            { label: "Attended",  value: attended_, color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
            { label: "Absent",    value: absent_,   color: "bg-red-50    border-red-200    text-red-600"      },
          ].map(({ label, value, color }) => (
            <div key={label} className={`rounded-2xl border p-4 text-center ${color}`}>
              <p className="text-2xl font-black">{value}</p>
              <p className="text-xs font-semibold">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {p.status === "DRAFT" && (
          <>
            <button onClick={() => { setPublishMode("all"); setEnrollMode(null); }} disabled={busy} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              Publish &amp; Enrol
            </button>
            <button onClick={handleDelete} disabled={busy} className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
              Delete
            </button>
          </>
        )}
        {p.status === "PUBLISHED" && (
          <>
            <button onClick={() => { setEnrollMode("all"); setPublishMode(null); }} disabled={busy} className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Enrol More
            </button>
            <button onClick={handleComplete} disabled={busy} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              Mark Completed
            </button>
            <button onClick={handleCancel} disabled={busy} className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
              Cancel Program
            </button>
          </>
        )}
      </div>

      {/* Publish / Enrol panel */}
      {(publishMode || enrollMode) && (
        <EnrolPanel
          mode={publishMode ?? enrollMode!}
          onModeChange={publishMode ? setPublishMode : setEnrollMode}
          deptIds={deptIds}
          onDeptIds={setDeptIds}
          empIds={empIds}
          onEmpIds={setEmpIds}
          onConfirm={publishMode ? handlePublish : handleEnrollMore}
          onCancel={() => { setPublishMode(null); setEnrollMode(null); }}
          busy={busy}
          label={publishMode ? "Publish & Enrol" : "Enrol Employees"}
        />
      )}

      {/* Enrollment list */}
      {enrollments.length > 0 && (
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="border-b bg-slate-50 px-5 py-3">
            <h2 className="font-bold text-slate-800">Enrollments ({enrollments.length})</h2>
          </div>
          <div className="divide-y">
            {enrollments.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 text-sm">{e.employeeName}</p>
                  <p className="text-xs text-slate-400">{e.employeeNumber}{e.departmentName ? ` · ${e.departmentName}` : ""}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${E_STATUS[e.status]}`}>{e.status}</span>
                {p.status === "PUBLISHED" && e.status !== "CANCELLED" && (
                  <div className="flex gap-1">
                    {e.status !== "ATTENDED" && (
                      <button
                        onClick={() => attended.mutate({ programId: progId, enrollmentId: e.id })}
                        disabled={attended.isPending}
                        title="Mark attended"
                        className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        {attended.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                      </button>
                    )}
                    {e.status !== "ABSENT" && (
                      <button
                        onClick={() => absent.mutate({ programId: progId, enrollmentId: e.id })}
                        disabled={absent.isPending}
                        title="Mark absent"
                        className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                      >
                        <UserMinus className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => cancelE.mutate({ programId: progId, enrollmentId: e.id })}
                      disabled={cancelE.isPending}
                      title="Cancel enrollment"
                      className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-50"
                    >
                      <UserX className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Enrol panel ───────────────────────────────────────────────────────────────

function EnrolPanel({
  mode, onModeChange, deptIds, onDeptIds, empIds, onEmpIds,
  onConfirm, onCancel, busy, label,
}: {
  mode: "all" | "dept" | "specific";
  onModeChange: (m: "all" | "dept" | "specific") => void;
  deptIds: string; onDeptIds: (v: string) => void;
  empIds: string; onEmpIds: (v: string) => void;
  onConfirm: () => void; onCancel: () => void;
  busy: boolean; label: string;
}) {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-4">
      <h3 className="font-bold text-slate-800">Select who to enrol</h3>

      <div className="flex flex-wrap gap-2">
        {(["all", "dept", "specific"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onModeChange(m)}
            className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold border transition-colors ${
              mode === m ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-slate-600 hover:border-emerald-400"
            }`}
          >
            <Users className="h-4 w-4" />
            {m === "all" ? "All Employees" : m === "dept" ? "By Department" : "Specific Employees"}
          </button>
        ))}
      </div>

      {mode === "dept" && (
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Department IDs (comma-separated)</span>
          <input className="input" value={deptIds} onChange={(e) => onDeptIds(e.target.value)} placeholder="e.g. 1, 3, 7" disabled={busy} />
        </label>
      )}
      {mode === "specific" && (
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Employee IDs (comma-separated)</span>
          <input className="input" value={empIds} onChange={(e) => onEmpIds(e.target.value)} placeholder="e.g. 12, 45, 99" disabled={busy} />
        </label>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-white disabled:opacity-50">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={busy} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {label}
        </button>
      </div>
    </div>
  );
}
